import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useMyWhatsappAlerts, useSaveMyWhatsappAlerts } from '../../api/platform-alerts';

const WHATSAPP_NUMBER = /^\+\d{8,15}$/;

/**
 * "WhatsApp alerts" on the Profile page: the member's WhatsApp number and an
 * explicit opt-in (Meta requires it) for account and billing alerts from
 * NeuroEdge Connect. Owners and admins get those alerts; without opting in
 * they come by email. The number is the profile phone.
 */
export function WhatsappAlertsCard() {
  const { refreshMe } = useAuth();
  const prefs = useMyWhatsappAlerts();
  const save = useSaveMyWhatsappAlerts();
  const toast = useToast();
  const [phone, setPhone] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (prefs.data) {
      setPhone(prefs.data.phone ?? '');
      setOptIn(prefs.data.optedIn);
    }
  }, [prefs.data]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const normalized = phone.trim() ? `+${phone.replace(/\D/g, '')}` : '';
    if (optIn && (!phone.trim().startsWith('+') || !WHATSAPP_NUMBER.test(normalized))) {
      setError('Enter your WhatsApp number with the country code, e.g. +91 98765 43210.');
      return;
    }
    setError(undefined);
    try {
      const saved = await save.mutateAsync({ phone: phone.trim() || null, optIn });
      await refreshMe();
      toast.success(
        saved.optedIn
          ? 'You will get account and billing alerts on WhatsApp.'
          : 'WhatsApp alerts are off - they will come by email.',
      );
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setError(fields.phone ?? (err instanceof Error ? err.message : 'Could not save.'));
    }
  }

  return (
    <Card title="WhatsApp alerts">
      {prefs.isPending ? (
        <PageState kind="loading" />
      ) : prefs.isError ? (
        <PageState
          kind="error"
          message={prefs.error.message}
          onRetry={() => void prefs.refetch()}
        />
      ) : (
        <form
          onSubmit={(e) => void onSubmit(e)}
          noValidate
          style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 480 }}
        >
          <p
            style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
          >
            Owners and admins get invoices, payment confirmations, renewal and trial reminders and
            WhatsApp connection warnings from NeuroEdge Connect. Turn this on to get them on
            WhatsApp instead of email.
          </p>
          <TextField
            label="WhatsApp number"
            placeholder="+91 98765 43210"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setError(undefined);
            }}
            error={error}
          />
          <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => {
                setOptIn(e.target.checked);
                setError(undefined);
              }}
              style={{ marginTop: 3 }}
            />
            <span>
              Send me account and billing alerts on WhatsApp. I can turn this off here any time.
            </span>
          </label>
          <div>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
